import React, { useEffect, useState } from 'react';
import { BadgePercent, CheckCircle2, FileCheck2, GraduationCap, Landmark, UserPlus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import PersonalDataSection from '../components/membership/PersonalDataSection';
import CompanyDataSection from '../components/membership/CompanyDataSection';
import LegalRepresentativeSection from '../components/membership/LegalRepresentativeSection';
import DeclarationSection from '../components/membership/DeclarationSection';
import DocumentsUploadSection from '../components/membership/DocumentsUploadSection';
import PrivacyConsentsSection from '../components/membership/PrivacyConsentsSection';
import LoadingButton from '../components/LoadingButton';
import SuccessModal from '../components/SuccessModal';
import { useToast } from '../contexts/ToastContext';

const BecomeMemberPage: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  // Form data state
  const [formData, setFormData] = useState({
    // Dati personali
    firstName: '',
    lastName: '',
    birthPlace: '',
    birthProvince: '',
    birthDate: '',
    role: '',
    
    // Dati aziendali
    businessName: '',
    vatNumber: '',
    fiscalCode: '',
    street: '',
    number: '',
    city: '',
    province: '',
    zipCode: '',
    phone: '',
    fax: '',
    mobile: '',
    email: '',
    website: '',
    pec: '',
    iban: '',
    mainActivity: '',
    
    // Rappresentante legale
    hasRepresentative: false,
    repFirstName: '',
    repLastName: '',
    repBirthPlace: '',
    repBirthProvince: '',
    repBirthDate: '',
    repPosition: '',
    repMobile: '',
  });

  // Documents state
  const [documents, setDocuments] = useState({
    identity: null as File | null,
    authorization: null as File | null,
    businessRegistry: null as File | null,
    representativeDocument: null as File | null,
  });

  // Declarations state
  const [declarations, setDeclarations] = useState({
    acceptsAssociationPolicy: false,
    acceptsProfessionalConduct: false,
  });

  // Privacy consents state
  const [consents, setConsents] = useState({
    dataProcessing: false,
    promotionalTraditional: false,
    promotionalElectronic: false,
  });

  const handleFieldChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user types
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleFileChange = (field: string, file: File | null) => {
    setDocuments(prev => ({ ...prev, [field]: file }));
    // Clear error when user selects file
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleDeclarationChange = (field: string, value: boolean) => {
    setDeclarations(prev => ({ ...prev, [field]: value }));
    // Clear error when user checks
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleConsentChange = (field: string, value: boolean) => {
    setConsents(prev => ({ ...prev, [field]: value }));
    // Clear error when user checks
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Validazione dati personali
    if (!formData.firstName.trim()) newErrors.firstName = 'Nome obbligatorio';
    if (!formData.lastName.trim()) newErrors.lastName = 'Cognome obbligatorio';
    if (!formData.birthPlace.trim()) newErrors.birthPlace = 'Luogo di nascita obbligatorio';
    if (!formData.birthProvince.trim()) newErrors.birthProvince = 'Provincia obbligatoria';
    else if (formData.birthProvince.length !== 2) newErrors.birthProvince = 'Inserire sigla provincia (2 caratteri)';
    if (!formData.birthDate) newErrors.birthDate = 'Data di nascita obbligatoria';
    if (!formData.role) newErrors.role = 'Selezionare il ruolo';

    // Validazione dati aziendali
    if (!formData.businessName.trim()) newErrors.businessName = 'Ragione sociale obbligatoria';
    
    if (!formData.vatNumber.trim()) {
      newErrors.vatNumber = 'Partita IVA obbligatoria';
    } else if (!/^\d{11}$/.test(formData.vatNumber)) {
      newErrors.vatNumber = 'Partita IVA non valida (11 cifre)';
    }
    
    if (!formData.fiscalCode.trim()) {
      newErrors.fiscalCode = 'Codice fiscale obbligatorio';
    } else if (!/^[A-Z0-9]{11,16}$/.test(formData.fiscalCode)) {
      newErrors.fiscalCode = 'Codice fiscale non valido';
    }
    
    if (!formData.street.trim()) newErrors.street = 'Indirizzo obbligatorio';
    if (!formData.number.trim()) newErrors.number = 'Numero civico obbligatorio';
    if (!formData.city.trim()) newErrors.city = 'Città obbligatoria';
    if (!formData.province.trim()) newErrors.province = 'Provincia obbligatoria';
    else if (formData.province.length !== 2) newErrors.province = 'Inserire sigla provincia (2 caratteri)';
    
    if (!formData.zipCode.trim()) {
      newErrors.zipCode = 'CAP obbligatorio';
    } else if (!/^\d{5}$/.test(formData.zipCode)) {
      newErrors.zipCode = 'CAP non valido (5 cifre)';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email obbligatoria';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Email non valida';
    }
    
    if (formData.pec && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.pec)) {
      newErrors.pec = 'PEC non valida';
    }
    
    if (formData.iban && !/^IT\d{2}[A-Z]\d{10}[A-Z0-9]{12}$/.test(formData.iban)) {
      newErrors.iban = 'IBAN non valido';
    }
    
    if (!formData.mainActivity.trim()) newErrors.mainActivity = 'Descrizione attività obbligatoria';

    // Validazione rappresentante legale (se presente)
    if (formData.hasRepresentative) {
      if (!formData.repFirstName.trim()) newErrors.repFirstName = 'Nome rappresentante obbligatorio';
      if (!formData.repLastName.trim()) newErrors.repLastName = 'Cognome rappresentante obbligatorio';
      if (!formData.repBirthPlace.trim()) newErrors.repBirthPlace = 'Luogo di nascita obbligatorio';
      if (!formData.repBirthProvince.trim()) newErrors.repBirthProvince = 'Provincia obbligatoria';
      else if (formData.repBirthProvince.length !== 2) newErrors.repBirthProvince = 'Inserire sigla provincia (2 caratteri)';
      if (!formData.repBirthDate) newErrors.repBirthDate = 'Data di nascita obbligatoria';
      if (!formData.repPosition.trim()) newErrors.repPosition = 'Qualifica obbligatoria';
    }

    // Validazione dichiarazioni
    if (!declarations.acceptsAssociationPolicy) {
      newErrors.acceptsAssociationPolicy = 'Devi accettare di sostenere la politica dell\'Associazione';
    }
    if (!declarations.acceptsProfessionalConduct) {
      newErrors.acceptsProfessionalConduct = 'Devi accettare i principi di condotta professionale';
    }

    // Validazione documenti
    if (!documents.identity) newErrors.identity = 'Documento identità obbligatorio';
    if (!documents.authorization) newErrors.authorization = 'Autorizzazione obbligatoria';
    if (!documents.businessRegistry) newErrors.businessRegistry = 'Visura camerale obbligatoria';

    // Validazione consensi
    if (!consents.dataProcessing) {
      newErrors.dataProcessing = 'Il consenso al trattamento dei dati è obbligatorio';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      const errorCount = Object.keys(errors).length;
      toast.error(`Correggi ${errorCount} ${errorCount === 1 ? 'errore' : 'errori'} nel modulo`);

      // Scroll to first error
      setTimeout(() => {
        const firstErrorElement = document.querySelector('[class*="border-red"]');
        if (firstErrorElement) {
          firstErrorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
      return;
    }

    setLoading(true);

    try {
      const formDataToSend = new FormData();

      // Aggiungi tutti i campi del form
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSend.append(key, value.toString());
      });

      // Aggiungi le dichiarazioni
      formDataToSend.append('acceptsAssociationPolicy', declarations.acceptsAssociationPolicy.toString());
      formDataToSend.append('acceptsProfessionalConduct', declarations.acceptsProfessionalConduct.toString());

      // Aggiungi i consensi
      formDataToSend.append('dataProcessing', consents.dataProcessing.toString());
      formDataToSend.append('promotionalTraditional', consents.promotionalTraditional.toString());
      formDataToSend.append('promotionalElectronic', consents.promotionalElectronic.toString());

      // Aggiungi i documenti
      if (documents.identity) formDataToSend.append('identity', documents.identity);
      if (documents.authorization) formDataToSend.append('authorization', documents.authorization);
      if (documents.businessRegistry) formDataToSend.append('businessRegistry', documents.businessRegistry);
      if (documents.representativeDocument) formDataToSend.append('representativeDocument', documents.representativeDocument);

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api'}/memberships`, {
        method: 'POST',
        body: formDataToSend,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Errore durante l\'invio della richiesta');
      }

      // Mostra modale di successo
      setShowSuccessModal(true);

    } catch (error) {
      console.error('Errore invio form:', error);
      toast.error(error instanceof Error ? error.message : 'Errore durante l\'invio della richiesta');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false);
    navigate('/');
  };

  // Sezione in vista, evidenziata nell'indice laterale
  const [sezioneAttiva, setSezioneAttiva] = useState(SEZIONI[0].id);
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return;
    const osservatore = new IntersectionObserver(
      (voci) => {
        const visibili = voci
          .filter((v) => v.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visibili[0]) setSezioneAttiva(visibili[0].target.id);
      },
      { rootMargin: '-25% 0px -65% 0px' },
    );
    SEZIONI.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) osservatore.observe(el);
    });
    return () => osservatore.disconnect();
  }, []);

  // Errori per sezione, per segnalarli nell'indice laterale
  const erroriPerSezione = (id: string) =>
    SEZIONI.find((s) => s.id === id)!.campi.filter((campo) => errors[campo]).length;

  const vaiASezione = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <>
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 pb-16">
        {/* Testata */}
        <section className="relative overflow-hidden bg-primary-950">
          <div className="absolute inset-y-0 right-0 w-full lg:w-[58%]">
            <img
              src="/images/diventa-socio/firma.webp"
              srcSet="/images/diventa-socio/firma-1280.webp 1280w, /images/diventa-socio/firma.webp 2400w"
              sizes="(min-width: 1024px) 58vw, 100vw"
              alt=""
              aria-hidden="true"
              fetchPriority="high"
              className="h-full w-full object-cover"
            />
          </div>
          <div className="absolute inset-0 bg-primary-950/80 lg:bg-transparent lg:bg-gradient-to-r lg:from-primary-950 lg:from-[42%] lg:via-primary-950/70 lg:via-[58%] lg:to-transparent" />

          <div className="relative container-custom pt-14 pb-28 md:pt-20 md:pb-32">
            <div className="max-w-2xl motion-safe:animate-fade-in">
              <div className="mb-6 inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 ring-1 ring-white/20 backdrop-blur-sm">
                <UserPlus className="h-4 w-4 text-primary-300" aria-hidden="true" />
                <span className="text-sm font-semibold text-white">Iscrizione</span>
              </div>
              <h1 className="mb-5 text-4xl md:text-6xl font-bold tracking-tight text-white">
                Diventa Associato UNASCA
              </h1>
              <p className="max-w-xl text-base md:text-lg leading-relaxed text-white/80">
                Compila il modulo di iscrizione per entrare a far parte dell'Unione Nazionale Autoscuole
                e Studi di Consulenza Automobilistica. Riceverai una conferma via email e sarai ricontattato
                dopo la verifica della documentazione.
              </p>
              <ul className="mt-7 flex flex-wrap gap-2 text-xs text-white/85">
                {[
                  { testo: 'Rappresentanza nelle istituzioni', Icona: Landmark },
                  { testo: 'Formazione e aggiornamento', Icona: GraduationCap },
                  { testo: 'Convenzioni riservate', Icona: BadgePercent },
                ].map(({ testo, Icona }) => (
                  <li key={testo} className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1.5 ring-1 ring-white/15">
                    <Icona className="h-3.5 w-3.5 text-primary-300" aria-hidden="true" />
                    {testo}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="pointer-events-none absolute bottom-32 right-10 hidden -rotate-6 text-right text-white lg:block xl:right-16" aria-hidden="true">
            <span className="block text-4xl leading-tight drop-shadow-lg" style={{ fontFamily: "'Caveat', cursive" }}>
              Benvenuto<br />in UNASCA
            </span>
            <svg viewBox="0 0 220 16" className="ml-auto mt-1 w-44 text-primary-400" fill="none">
              <path d="M3 12 C 60 2, 140 2, 217 8" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
            </svg>
          </div>
        </section>

        <div className="container-custom relative -mt-16">
          <div className="grid gap-8 lg:grid-cols-12">
            {/* Indice del modulo */}
            <aside className="lg:col-span-4 xl:col-span-3">
              <div className="space-y-4 lg:sticky lg:top-24">
                <nav aria-label="Sezioni del modulo" className="rounded-2xl bg-white p-5 shadow-xl ring-1 ring-black/5 dark:bg-gray-900 dark:ring-white/10">
                  <p className="mb-4 text-sm font-semibold text-gray-900 dark:text-white">Il modulo in 6 parti</p>
                  <ol className="relative space-y-1">
                    <span className="absolute bottom-3 left-[0.9rem] top-3 w-0.5 bg-gray-200 dark:bg-gray-700" aria-hidden="true" />
                    {SEZIONI.map((sezione, i) => {
                      const attiva = sezioneAttiva === sezione.id;
                      const nErrori = erroriPerSezione(sezione.id);
                      return (
                        <li key={sezione.id} className="relative">
                          <button
                            type="button"
                            onClick={() => vaiASezione(sezione.id)}
                            aria-current={attiva ? 'step' : undefined}
                            className={`flex w-full items-center gap-3 rounded-xl px-1.5 py-2 text-left text-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${
                              attiva ? 'bg-primary-50 font-semibold text-primary-900 dark:bg-primary-900/30 dark:text-primary-100' : 'text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-800'
                            }`}
                          >
                            <span
                              className={`relative z-10 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-xs font-semibold ring-4 ring-white dark:ring-gray-900 ${
                                nErrori > 0 ? 'bg-red-600 text-white' : attiva ? 'bg-primary-600 text-white' : 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                              }`}
                            >
                              {nErrori > 0 ? '!' : i + 1}
                            </span>
                            <span className="flex-1">{sezione.titolo}</span>
                            {nErrori > 0 && (
                              <span className="rounded-full bg-red-50 px-2 py-0.5 text-xs font-semibold text-red-700 dark:bg-red-900/30 dark:text-red-300">
                                {nErrori === 1 ? '1 da correggere' : `${nErrori} da correggere`}
                              </span>
                            )}
                          </button>
                        </li>
                      );
                    })}
                  </ol>
                </nav>

                <div className="hidden rounded-2xl bg-white p-5 shadow-sm ring-1 ring-black/5 dark:bg-gray-900 dark:ring-white/10 lg:block">
                  <p className="mb-3 flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-white">
                    <FileCheck2 className="h-4 w-4 text-primary-600 dark:text-primary-400" aria-hidden="true" />
                    Tieni pronti
                  </p>
                  <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                    {['Documento di identità', 'Autorizzazione', 'Visura camerale', 'Partita IVA e codice fiscale dello studio'].map((voce) => (
                      <li key={voce} className="flex items-start gap-2">
                        <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary-600 dark:text-primary-400" aria-hidden="true" />
                        {voce}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </aside>

            {/* Modulo */}
            <form onSubmit={handleSubmit} className="space-y-8 lg:col-span-8 xl:col-span-9">
              <div id="sezione-dati-personali" className="scroll-mt-24">
                <PersonalDataSection
                  formData={formData}
                  errors={errors}
                  onChange={handleFieldChange}
                />
              </div>

              <div id="sezione-dati-aziendali" className="scroll-mt-24">
                <CompanyDataSection
                  formData={formData}
                  errors={errors}
                  onChange={handleFieldChange}
                />
              </div>

              <div id="sezione-vita-associativa" className="scroll-mt-24">
                <LegalRepresentativeSection
                  formData={formData}
                  errors={errors}
                  onChange={handleFieldChange}
                />
              </div>

              <div id="sezione-dichiarazioni" className="scroll-mt-24">
                <DeclarationSection
                  formData={declarations}
                  errors={errors}
                  onChange={handleDeclarationChange}
                />
              </div>

              <div id="sezione-documenti" className="scroll-mt-24">
                <DocumentsUploadSection
                  documents={documents}
                  errors={errors}
                  onFileChange={handleFileChange}
                />
              </div>

              <div id="sezione-privacy" className="scroll-mt-24">
                <PrivacyConsentsSection
                  consents={consents}
                  errors={errors}
                  onChange={handleConsentChange}
                />
              </div>

              {/* Submit Button */}
              <div className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-primary-950 via-primary-900 to-primary-800 p-6 sm:p-8">
                <div className="flex flex-col items-start gap-5 md:flex-row md:items-center md:justify-between">
                  <div>
                    <p className="text-lg font-semibold text-white">Tutto pronto?</p>
                    <p className="mt-1 text-sm text-white/75">
                      Controlliamo i campi prima dell'invio e ti indichiamo le parti da correggere.
                    </p>
                  </div>
                  <LoadingButton
                    type="submit"
                    loading={loading}
                    className="px-8 py-4 bg-primary-500 hover:bg-primary-400 text-white font-semibold rounded-full transition-colors text-lg shadow-lg"
                  >
                    Invia Richiesta di Iscrizione
                  </LoadingButton>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Success Modal */}
      <SuccessModal
        isOpen={showSuccessModal}
        onClose={handleCloseSuccessModal}
        title="Richiesta Inviata con Successo!"
        message={`
          <p class="mb-4">Gentile nuovo associato,</p>
          <p class="mb-4">La ringraziamo per aver inviato la richiesta di iscrizione a UNASCA.</p>
          <p class="mb-4">Abbiamo ricevuto correttamente la Sua domanda e i documenti allegati. A breve riceverà una <strong>email di conferma</strong> all'indirizzo indicato.</p>
          <p>Il nostro ufficio procederà alla verifica della documentazione e La ricontatteremo al più presto per comunicarLe l'esito della Sua richiesta.</p>
        `}
      />
    </>
  );
};

// Sezioni del modulo con i campi che contengono, per l'indice e gli errori
const SEZIONI: { id: string; titolo: string; campi: string[] }[] = [
  { id: 'sezione-dati-personali', titolo: 'Dati Rappresentante Legale', campi: ['firstName', 'lastName', 'birthPlace', 'birthProvince', 'birthDate', 'role'] },
  { id: 'sezione-dati-aziendali', titolo: 'Dati Aziendali', campi: ['businessName', 'vatNumber', 'fiscalCode', 'street', 'number', 'city', 'province', 'zipCode', 'phone', 'fax', 'mobile', 'email', 'website', 'pec', 'iban', 'mainActivity'] },
  { id: 'sezione-vita-associativa', titolo: 'Rappresentante alla vita associativa', campi: ['repFirstName', 'repLastName', 'repBirthPlace', 'repBirthProvince', 'repBirthDate', 'repPosition', 'repMobile'] },
  { id: 'sezione-dichiarazioni', titolo: 'Dichiarazioni', campi: ['acceptsAssociationPolicy', 'acceptsProfessionalConduct'] },
  { id: 'sezione-documenti', titolo: 'Documenti Allegati', campi: ['identity', 'authorization', 'businessRegistry', 'representativeDocument'] },
  { id: 'sezione-privacy', titolo: 'Consensi Privacy', campi: ['dataProcessing', 'promotionalTraditional', 'promotionalElectronic'] },
];

export default BecomeMemberPage;
